import parse, { domToReact } from 'html-react-parser';
import dynamic from 'next/dynamic';

import { toFallbackImageAltText } from 'lib/image';
import { forceHttps, sanitize, toInternalLink } from 'lib/links';
import { isUrlValidForInternalUsage } from 'lib/string';

import {
  doesAnyDomNodeParentContainName,
  isLineBreaker,
  isYoutubeIframe,
  strip,
} from './htmlParserUtils';

import HTML_PARSER from 'constants/htmlParser';

const Text = dynamic(() => import('shopper/components/Text'));
const NextLink = dynamic(() => import('next/link'));
const Anchor = dynamic(() => import('components/Anchor'));
const YoutubeFacade = dynamic(() => import('components/YoutubeFacade'));
const Image = dynamic(() => import('components/Image'));
const ListGroup = dynamic(() => import('components/List/ListGroup'));
const ListItem = dynamic(() => import('components/List/ListItem'));

const offerAdditionalInfosTextOptions = {
  trim: true,
  replace: (domNode) => {
    const { name, attribs, children, type, data } = domNode;

    /**
     * We ignores unmapped nodes because some texts aren't wrapped to a tag.
     * So, because of this, we needs explicit to return the text
     */
    if (type === 'text' && data && !isLineBreaker(data)) {
      if (
        doesAnyDomNodeParentContainName(
          domNode?.parent,
          HTML_PARSER.NAME_PARAGRAPH
        )
      ) {
        return <span>{strip(data)}</span>;
      }

      if (domNode?.parent === null) {
        return <Text as="span">{strip(data)}</Text>;
      }

      return data.trim();
    }

    if (name === HTML_PARSER.NAME_BOLD || name === HTML_PARSER.NAME_STRONG) {
      return (
        <Text as="span" bold>
          {domToReact(children, offerAdditionalInfosTextOptions)}
        </Text>
      );
    }

    if (name === HTML_PARSER.NAME_PARAGRAPH) {
      return (
        <Text as="span">
          {domToReact(children, offerAdditionalInfosTextOptions)}
        </Text>
      );
    }

    if (name === HTML_PARSER.NAME_EM) {
      return (
        <Text as="em" italic>
          {domToReact(children, offerAdditionalInfosTextOptions)}
        </Text>
      );
    }

    if (name === HTML_PARSER.NAME_LIST_ITEM) {
      return (
        <ListItem>
          {domToReact(children, offerAdditionalInfosTextOptions)}
        </ListItem>
      );
    }

    if (
      [HTML_PARSER.NAME_UNORDERED_LIST, HTML_PARSER.NAME_ORDERED_LIST].includes(
        name
      )
    ) {
      const isOrderedList = children[0].name === HTML_PARSER.NAME_ORDERED_LIST;

      return (
        <ListGroup
          className="mb-1 space-y-2"
          type={isOrderedList ? 'ordered' : 'unordered'}
        >
          {domToReact(children, offerAdditionalInfosTextOptions)}
        </ListGroup>
      );
    }

    if (name === HTML_PARSER.NAME_BR) {
      return <br />;
    }

    if (name === HTML_PARSER.NAME_HREF) {
      const { href } = attribs;

      if (isUrlValidForInternalUsage(href)) {
        return (
          <NextLink
            href={toInternalLink(sanitize(href))}
            prefetch={false}
            passHref
          >
            <Anchor>
              {domToReact(children, offerAdditionalInfosTextOptions)}
            </Anchor>
          </NextLink>
        );
      }

      return (
        <Anchor
          href={forceHttps(href)}
          rel="noreferrer noopener"
          target="_blank"
        >
          {domToReact(children, offerAdditionalInfosTextOptions)}
        </Anchor>
      );
    }

    if (name === HTML_PARSER.NAME_IMAGE) {
      const { alt, src } = attribs ?? {};

      return (
        <Image
          alt={toFallbackImageAltText(alt)}
          className="mx-auto my-4 h-auto w-full"
          height={192}
          src={src}
          width={343}
        />
      );
    }

    if (name === HTML_PARSER.NAME_IFRAME && isYoutubeIframe(attribs.src)) {
      return (
        <YoutubeFacade
          className="mb-10"
          src={attribs.src}
          title={attribs.title}
          full
        />
      );
    }

    return null;
  },
};

const toOfferAdditionalInfosText = (offerAdditionalInfoText) =>
  parse(offerAdditionalInfoText, offerAdditionalInfosTextOptions);

export default toOfferAdditionalInfosText;
